/**
 * AdmissionsReports — programme demand, the funnel and where applicants come
 * from. Every section exports to CSV.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KPI = ({ label, value, suffix, tone = 'text-slate-900', testId }) => (
  <div className="px-5 py-4 border border-slate-200 rounded-xl bg-white" data-testid={testId}>
    <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-400">{label}</p>
    <p className={`mt-1.5 text-2xl font-bold tabular-nums ${tone}`}>
      {value}{suffix || ''}
    </p>
  </div>
);

const exportSection = async (tenantId, section, intakeId) => {
  try {
    const qs = new URLSearchParams({ section, ...(intakeId ? { intake_id: intakeId } : {}) });
    const res = await fetch(
      `${API_URL}/api/school/admissions/tenants/${tenantId}/reports.csv?${qs}`,
      { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `admissions-${section}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(e.message);
  }
};

const Panel = ({ title, subtitle, section, tenantId, intakeId, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {section && (
          <Button variant="outline" size="sm" data-testid={`export-${section}-btn`}
                  onClick={() => exportSection(tenantId, section, intakeId)}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const BarList = ({ rows, empty, testPrefix }) => {
  if (!rows?.length) return <p className="text-sm text-slate-400 py-3">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.key || r.label} data-testid={`${testPrefix}-${r.key || r.label}`}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-slate-700 truncate">{r.label}</span>
            <span className="text-slate-500 tabular-nums shrink-0">
              {r.count} <span className="text-slate-400 text-xs">· {r.share}%</span>
            </span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-slate-900 transition-all duration-700"
                 style={{ width: `${max ? (r.count / max) * 100 : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const AdmissionsReports = ({ tenantId }) => {
  const [intakeId, setIntakeId] = useState('');

  const { data: intakeData } = useQuery({
    queryKey: ['admissions-intakes', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/intakes`)).data,
    retry: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admissions-reports', tenantId, intakeId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/reports`,
      { params: intakeId ? { intake_id: intakeId } : {} })).data,
  });

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full" />)}</div>;
  }

  const r = data?.rates || {};
  const intakes = intakeData?.items || [];

  return (
    <div className="space-y-4" data-testid="admissions-reports">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={intakeId} onChange={(e) => setIntakeId(e.target.value)}
                data-testid="reports-intake-filter"
                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All intakes</option>
          {intakes.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <span className="text-xs text-slate-500">
          {r.applications || 0} applications · {r.submitted || 0} submitted
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Acceptance rate" value={r.acceptance_rate || 0} suffix="%"
             tone="text-emerald-600" testId="kpi-acceptance-rate" />
        <KPI label="Registration rate" value={r.registration_rate || 0} suffix="%"
             tone="text-indigo-600" testId="kpi-registration-rate" />
        <KPI label="Decline rate" value={r.decline_rate || 0} suffix="%"
             tone="text-rose-600" testId="kpi-decline-rate" />
        <KPI label="Applied → registered" value={r.conversion_rate || 0} suffix="%"
             testId="kpi-conversion-rate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Programme demand" testId="reports-programmes"
               subtitle="Applications, offers and registrations per programme"
               section="programmes" tenantId={tenantId} intakeId={intakeId}>
          {!data?.programmes?.length ? (
            <p className="text-sm text-slate-400 py-3">No applications against a programme yet.</p>
          ) : (
            <div className="space-y-3">
              {data.programmes.map((p) => (
                <div key={p.id} className="border border-slate-100 rounded-lg px-4 py-3"
                     data-testid={`programme-demand-${p.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-slate-800">{p.name}</span>
                    <span className="text-xs text-slate-500 tabular-nums">
                      {p.applications} applied · {p.share}% of demand
                    </span>
                  </div>
                  <div className="mt-2 flex gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      {p.accepted} accepted · {p.acceptance_rate}%
                    </Badge>
                    <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                      {p.registered} registered · {p.registration_rate}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Where applicants come from" testId="reports-sources"
               subtitle="How they heard about the school"
               section="sources" tenantId={tenantId} intakeId={intakeId}>
          <BarList rows={data?.sources} testPrefix="source-row"
                   empty="Nobody has told us how they found the school yet." />
        </Panel>

        <Panel title="Applications by intake" testId="reports-intakes"
               section="intakes" tenantId={tenantId} intakeId={intakeId}>
          {!data?.intakes?.length ? (
            <p className="text-sm text-slate-400 py-3">No intake has applications yet.</p>
          ) : (
            <div className="space-y-2">
              {data.intakes.map((i) => (
                <div key={i.id} className="flex items-baseline justify-between gap-3 text-sm"
                     data-testid={`intake-row-${i.id}`}>
                  <span className="text-slate-700 truncate">
                    {i.name}
                    {i.is_open && <Badge className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 border-0">Open</Badge>}
                  </span>
                  <span className="text-slate-500 tabular-nums shrink-0">
                    {i.applications} · {i.acceptance_rate}% accepted · {i.registration_rate}% registered
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="The funnel" testId="reports-funnel"
               section="funnel" tenantId={tenantId} intakeId={intakeId}>
          <div className="space-y-2">
            {(data?.funnel || []).map((s) => (
              <div key={s.key} className="flex items-center justify-between text-sm"
                   data-testid={`funnel-${s.key}`}>
                <span className="text-slate-600">{s.label}</span>
                <span className="font-semibold text-slate-900 tabular-nums">{s.count}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Countries" testId="reports-countries" section="countries"
               tenantId={tenantId} intakeId={intakeId}>
          <BarList rows={data?.countries} testPrefix="country-row" empty="No country recorded yet." />
        </Panel>

        <Panel title="Campus & study mode" testId="reports-campuses">
          <div className="space-y-5">
            <BarList rows={data?.campuses} testPrefix="campus-row" empty="No campus recorded yet." />
            <BarList rows={data?.study_modes} testPrefix="mode-row" empty="No study mode recorded yet." />
          </div>
        </Panel>

        <Panel title="Applicant types" testId="reports-types" section="applicant_types"
               tenantId={tenantId} intakeId={intakeId}>
          <BarList rows={data?.applicant_types} testPrefix="type-row" empty="No applicant type recorded yet." />
          <div className="mt-4 flex gap-2 flex-wrap text-xs">
            <Badge variant="outline" data-testid="reports-sponsored">
              {data?.extras?.sponsored || 0} sponsored
            </Badge>
            <Badge variant="outline" data-testid="reports-international">
              {data?.extras?.international || 0} international
            </Badge>
            <Badge variant="outline">{r.withdrawn || 0} withdrawn</Badge>
            <Badge variant="outline">{r.deferred || 0} deferred</Badge>
            <Badge variant="outline">{r.waitlisted || 0} waitlisted</Badge>
          </div>
        </Panel>

        <Panel title="Registration & finance" testId="reports-finance" section="finance"
               tenantId={tenantId} intakeId={intakeId}>
          {!data?.finance?.length ? (
            <p className="text-sm text-slate-400 py-3">Nobody has reached registration yet.</p>
          ) : (
            <div className="space-y-2">
              {data.finance.map((f) => (
                <div key={f.key} className="flex items-center justify-between text-sm"
                     data-testid={`finance-${f.key}`}>
                  <span className="text-slate-600">{f.label}</span>
                  <span className="font-semibold text-slate-900 tabular-nums">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default AdmissionsReports;
